import React, { useState, useEffect } from 'react';
import { AlertTriangle, Shield, Clock, Users, Server, Database, Network, FileText, CheckCircle, XCircle } from 'lucide-react';
import IdeasPageHeader from './IdeasPageHeader';

const Guidelines = () => {
  const [activeTab, setActiveTab] = useState('guidelines');
  const [loading, setLoading] = useState(true);

  // State for all Guidelines data
  const [reviewerCriteria, setReviewerCriteria] = useState({});
  const [itpmReviewCriteria, setItpmReviewCriteria] = useState({});
  const [subconConstraints, setSubconConstraints] = useState({});
  const [itpmRisks, setItpmRisks] = useState({});
  const [goalDescriptions, setGoalDescriptions] = useState({});
  const [categories, setCategories] = useState({});
  const [thematicClusters, setThematicClusters] = useState({});

  // Fetch all Guidelines data from API
  useEffect(() => {
    fetchAllGuidelines();
  }, []);

  const fetchAllGuidelines = async () => {
    try {
      setLoading(true);

      // Fetch all data in parallel
      const [
        reviewCriteriaRes,
        itpmCriteriaRes,
        subconRes,
        risksRes,
        goalsRes,
        categoriesRes,
        clustersRes
      ] = await Promise.all([
        fetch('/api/guidelines/review_criteria'),
        fetch('/api/guidelines/itpm_criteria'),
        fetch('/api/guidelines/subcon_constraints'),
        fetch('/api/guidelines/itpm_risks'),
        fetch('/api/guidelines/goal_descriptions'),
        fetch('/api/guidelines/idea_categories'),
        fetch('/api/guidelines/thematic_clusters')
      ]);

      // Parse all responses
      const reviewCriteriaData = await reviewCriteriaRes.json();
      const itpmCriteriaData = await itpmCriteriaRes.json();
      const subconData = await subconRes.json();
      const risksData = await risksRes.json();
      const goalsData = await goalsRes.json();
      const categoriesData = await categoriesRes.json();
      const clustersData = await clustersRes.json();

      // Transform review criteria to object format
      const reviewCriteriaObj = {};
      reviewCriteriaData.criteria?.forEach(c => {
        reviewCriteriaObj[c.criterion_name] = c.description;
      });
      setReviewerCriteria(reviewCriteriaObj);

      // Transform ITPM criteria to object format
      const itpmCriteriaObj = {};
      itpmCriteriaData.criteria?.forEach(c => {
        itpmCriteriaObj[c.criterion_name] = c.description;
      });
      setItpmReviewCriteria(itpmCriteriaObj);

      // Transform subcon constraints to grouped object format
      const subconObj = {};
      subconData.constraints?.forEach(c => {
        if (!subconObj[c.constraint_type]) {
          subconObj[c.constraint_type] = [];
        }
        subconObj[c.constraint_type].push(c.constraint_item);
      });
      setSubconConstraints(subconObj);

      // Transform ITPM risks to object format
      const risksObj = {};
      risksData.risks?.forEach(r => {
        risksObj[r.risk_name] = {
          Description: r.description,
          Likelihood: r.likelihood,
          Impact: r.impact
        };
      });
      setItpmRisks(risksObj);

      // Transform goals to object format
      const goalsObj = {};
      goalsData.goals?.forEach(g => {
        goalsObj[g.goal_name] = g.description;
      });
      setGoalDescriptions(goalsObj);

      // Transform categories to object format
      const categoriesObj = {};
      categoriesData.categories?.forEach(c => {
        categoriesObj[c.category_name] = c.description;
      });
      setCategories(categoriesObj);

      // Transform clusters to object format
      const clustersObj = {};
      clustersData.clusters?.forEach(c => {
        clustersObj[c.cluster_name] = c.description;
      });
      setThematicClusters(clustersObj);

      setLoading(false);
    } catch (error) {
      console.error('Error fetching guidelines data:', error);
      setLoading(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--cares-surface-board)' }}>
        <div style={{ padding: '24px' }}>
          <IdeasPageHeader
            title="Guidelines & Rules Engine"
            description="Review criteria, constraints, and risk assessment framework"
            breadcrumbPath="Guidelines & Rules Engine"
          />
          <div style={{
            background: 'var(--cares-surface-card)',
            borderRadius: '8px',
            padding: '40px',
            textAlign: 'center',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <div style={{ fontSize: '18px', color: 'var(--cares-text-label)' }}>Loading guidelines data...</div>
          </div>
        </div>
      </div>
    );
  }

  const getRiskColor = (likelihood, impact) => {
    if ((likelihood === 'High' && impact === 'High') || (likelihood === 'Medium' && impact === 'High')) {
      return 'bg-red-100 text-red-800 border-red-300';
    } else if ((likelihood === 'High' && impact === 'Medium') || (likelihood === 'Medium' && impact === 'Medium')) {
      return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    } else {
      return 'bg-green-100 text-green-800 border-green-300';
    }
  };

  const getIconForConstraint = (type) => {
    switch (type) {
      case 'External System Dependencies':
        return <Network className="h-5 w-5 text-blue-600" />;
      case 'Data Availability and Privacy':
        return <Database className="h-5 w-5 text-purple-600" />;
      case 'Platform and Infrastructure Constraints':
        return <Server className="h-5 w-5 text-orange-600" />;
      case 'Operational Security Constraints':
        return <Shield className="h-5 w-5 text-red-600" />;
      default:
        return <FileText className="h-5 w-5 text-gray-600" />;
    }
  };

  const tabs = [
    { id: 'guidelines', label: 'Review Guidelines' },
    { id: 'itpm-review', label: 'ITPM Review' },
    { id: 'constraints', label: 'System Constraints' },
    { id: 'risks', label: 'Risk Assessment' },
    { id: 'goals', label: 'Strategic Goals' },
    { id: 'categories', label: 'Categories' }
  ];

  return (
    <div style={{ minHeight: '100vh', background: 'var(--cares-surface-board)' }}>
      <div style={{ padding: '24px' }}>
        {/* Professional Header with Breadcrumbs */}
        <IdeasPageHeader
          title="Guidelines & Rules Engine"
          description="Comprehensive review framework for IT project management"
          breadcrumbPath="Guidelines & Rules Engine"
        />

        <div className="max-w-7xl mx-auto space-y-6">

      <div className="w-full">
        {/* Fixed Tabs - Horizontal Layout */}
        <div className="flex bg-gray-200 rounded-lg p-1 mb-6 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-white text-gray-900 shadow-smm'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'guidelines' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-smm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                  <FileText className="h-6 w-6 text-blue-600" />
                  Reviewer Criteria
                </h2>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(reviewerCriteria).map(([key, value]) => (
                    <div key={key} className="p-4 bg-gray-50 rounded-lg border">
                      <h3 className="font-semibold text-gray-900 mb-2">{key}</h3>
                      <p className="text-sm text-gray-700">{value}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'itpm-review' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-smm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                  ITPM Review Criteria
                </h2>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(itpmReviewCriteria).map(([key, value]) => (
                    <div key={key} className="p-4 bg-green-50 rounded-lg border border-green-200">
                      <h3 className="font-semibold text-green-900 mb-2">{key}</h3>
                      <p className="text-sm text-green-800">{value}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'constraints' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-smm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                  <AlertTriangle className="h-6 w-6 text-orange-600" />
                  System Constraints & Dependencies
                </h2>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {Object.entries(subconConstraints).map(([category, items]) => (
                    <div key={category} className="space-y-4">
                      <div className="flex items-center gap-2 mb-3">
                        {getIconForConstraint(category)}
                        <h3 className="font-bold text-lg text-gray-900">{category}</h3>
                      </div>
                      <div className="space-y-3">
                        {items.map((item, index) => (
                          <div key={index} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg border-l-4 border-blue-500">
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium text-gray-900">{item}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'risks' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-smm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                  <AlertTriangle className="h-6 w-6 text-red-600" />
                  Risk Assessment Matrix
                </h2>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {Object.entries(itpmRisks).map(([risk, details]) => (
                    <div key={risk} className={`p-4 rounded-lg border-2 ${getRiskColor(details.Likelihood, details.Impact)}`}>
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="font-bold text-sm">{risk}</h3>
                        <div className="flex gap-2">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 border">
                            L: {details.Likelihood}
                          </span>
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 border">
                            I: {details.Impact}
                          </span>
                        </div>
                      </div>
                      <p className="text-sm">{details.Description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'goals' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-smm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                  <Users className="h-6 w-6 text-green-600" />
                  Strategic Goals & Thematic Clusters
                </h2>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Strategic Goals Column */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 mb-6">
                      <Users className="h-6 w-6 text-green-600" />
                      <h3 className="text-xl font-bold text-gray-900">Strategic Goals</h3>
                    </div>
                    <div className="space-y-4">
                      {Object.entries(goalDescriptions).map(([goal, description]) => (
                        <div key={goal} className="p-4 bg-green-50 rounded-lg border border-green-200">
                          <h4 className="font-bold text-green-900 mb-2 text-base">{goal}</h4>
                          <p className="text-sm text-green-800 leading-relaxed">{description}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Thematic Clusters Column */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 mb-6">
                      <Clock className="h-6 w-6 text-purple-600" />
                      <h3 className="text-xl font-bold text-gray-900">Thematic Clusters</h3>
                    </div>
                    <div className="space-y-4">
                      {Object.entries(thematicClusters).map(([cluster, description]) => (
                        <div key={cluster} className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                          <h4 className="font-bold text-purple-900 mb-2 text-base">{cluster}</h4>
                          <p className="text-sm text-purple-800 leading-relaxed">{description}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'categories' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-smm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                  <Database className="h-6 w-6 text-blue-600" />
                  Project Categories
                </h2>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {Object.entries(categories).map(([category, description]) => (
                    <div key={category} className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <h3 className="font-bold text-blue-900 mb-2">{category}</h3>
                      <p className="text-sm text-blue-800">{description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
        </div>
      </div>
    </div>
  );
};

export default Guidelines;